<template>
  <div class="h-full">
    <BaseTable
      v-if="paymentTypes"
      :headers="tableHeaders"
      :list="filteredPaymentTypes"
      :total-records="paymentTypesTotalCount"
      :page="paymentTypesPageNumber"
      :items-per-page="paymentTypesItemsPerPage"
      table-id="payment-types"
      is-saving-layout-hidden
      clickable
      hide-grouping
      :is-add-new-hidden="mode === 'view'"
      :disable-multiselect="mode === 'view'"
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNew"
      @refresh-data="onRefreshData"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters"
      @table-item-clicked="onEdit">
      <template #actions="{ item }" v-if="mode == 'edit'">
        <div class="flex items-center justify-center gap-1">
          <button
            v-if="item.isFromManager"
            class="p-1.5 text-red-400 rounded-full hover:bg-red-50"
            type="button"
            @click="onDeleteRow(item)">
            <IconTrashOutline class="size-4" />
          </button>
        </div>
      </template>
      <template #filters>
        <div class="grid grid-cols-3 mx-10 my-5">
          <!-- Col 1 -->
          <div class="space-y-4 justify-self-start">
            <div>
              <BaseTriStateToggle
                :label="$t('representative.isMapped')"
                name="isInactiveFilter"
                v-model="filterIsMapped" />
            </div>
          </div>
        </div>
      </template>
    </BaseTable>

    <DCPaymentTypeModalCreateEdit
      v-if="createEditModalActive && currentPaymentType"
      :view-only="mode === 'view'"
      :payment-type="currentPaymentType"
      :mode="createEditModalMode"
      :representative-id="representativeId"
      :existing-payment-types="paymentTypes"
      @close-modal="createEditModalActive = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useI18n } from 'vue-i18n';

import BaseTable from '@/components/common/BaseTable.vue';
import DCPaymentTypeModalCreateEdit from '@/components/traders/DCPaymentTypeModalCreateEdit.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import BaseTriStateToggle from '../common/BaseTriStateToggle.vue';

import { deepCopy } from '@/utils/data';
import { RecordMode, type TableFilterData } from '@/types/Base';
import { getDCPaymentTypesWithCondition } from '@/services/api/dataCollection';
import type { DCPaymentType, RepresentativeFiltersData } from '@/types/DataCollection';

const props = defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout?: boolean;
  resetState: boolean;
  representativeId: string;
}>();

const paymentTypes = defineModel<DCPaymentType[]>('paymentTypes');
const paymentTypeModified = defineModel<boolean>('isPaymentTypeModified', {
  default: false,
});
const filterIsMapped = ref<boolean | undefined>(undefined);

const originalPaymentTypes = ref<DCPaymentType[]>([]);
const paymentTypesTotalCount = ref<number>(0);
const paymentTypesItemsPerPage = ref(50);
const paymentTypesPageNumber = ref(1);

const filteredPaymentTypes = computed<DCPaymentType[]>(() => {
  if (!paymentTypes.value) return [];
  let list = paymentTypes.value.filter((x) => x.recordMode !== RecordMode.Deleted);

  return list;
});

watch(
  () => paymentTypes.value,
  (newData) => {
    paymentTypeModified.value =
      JSON.stringify(newData) !== JSON.stringify(originalPaymentTypes.value);
  },
  { deep: true }
);

async function loadPaymentTypes(
  params?: TableFilterData & {
    isMapped?: boolean;
  }
) {
  const queryParams = {
    representativeId: props.representativeId,
    page: params?.page || paymentTypesPageNumber.value,
    itemsPerPage: params?.itemsPerPage || paymentTypesItemsPerPage.value,
    query: params?.query || '',
    sortColumn: params?.column !== undefined ? tableHeaders[params.column]?.value : undefined,
    sortAscending: params?.order === 'asc' || params?.order === undefined,
    isMapped: params?.isMapped,
  };

  const res = await getDCPaymentTypesWithCondition(queryParams);
  if (res) {
    originalPaymentTypes.value = res.data;
    paymentTypes.value = deepCopy<DCPaymentType[]>(originalPaymentTypes.value);
    paymentTypesTotalCount.value = res.count;

    if (params?.page !== undefined) {
      paymentTypesPageNumber.value = params.page;
    }
    if (params?.itemsPerPage !== undefined) {
      paymentTypesItemsPerPage.value = params.itemsPerPage;
    }
  }
}

watch(
  () => props.resetState,
  async (newVal, oldVal) => {
    if (props.representativeId && (oldVal === undefined || (newVal && oldVal !== newVal))) {
      await loadPaymentTypes();
    }
  },
  { immediate: true }
);

const { t: $t } = useI18n();

const createEditModalActive = ref(false);
const createEditModalMode = ref<'add' | 'edit'>('add');
const currentPaymentType = ref<DCPaymentType | undefined>(undefined);

const tableHeaders = [
  { value: 'paymentType.name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'paymentType.code', text: $t('baseObject.code'), type: 'text', active: true },
  {
    value: 'paymentMethodRemotedCode',
    text: $t('representative.remotedCode'),
    type: 'text',
    active: true,
  },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

function initPaymentType(): DCPaymentType {
  return {
    id: uuidv4(),
    representativeId: props.representativeId,
    paymentMethodRemotedCode: '',
    paymentTypeId: '',
    paymentType: {
      id: '',
      name: '',
    },
    isActive: true,
    isFromManager: true,
    recordMode: RecordMode.Added,
  };
}

function onAddNew() {
  currentPaymentType.value = initPaymentType();
  createEditModalMode.value = 'add';
  createEditModalActive.value = true;
}

function onEdit(item: DCPaymentType) {
  currentPaymentType.value = deepCopy(item);
  createEditModalMode.value = 'edit';
  createEditModalActive.value = true;
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<DCPaymentType>();

function onDeleteRow(item: DCPaymentType) {
  deleteRowItem.value = deepCopy(item);
  modalDeleteRow.value = true;
}

async function onDeleteSingle() {
  if (!deleteRowItem.value || !paymentTypes.value) return;
  modalDeleteRow.value = false;
  const index = paymentTypes.value?.findIndex((x) => x.id === deleteRowItem.value?.id);
  if (index !== -1) {
    const existingItem = paymentTypes.value[index];
    if (existingItem.recordMode === RecordMode.Added) {
      paymentTypes.value.splice(index, 1);
    } else if (existingItem.recordMode === RecordMode.Modified) {
      existingItem.recordMode = RecordMode.Deleted;
    } else {
      existingItem.recordMode = RecordMode.Deleted;
    }
  }
}

function onDeleteMultiselect(items: DCPaymentType[]) {
  modalDeleteRow.value = false;

  items.forEach((item) => {
    if (!paymentTypes.value) return;
    if (item.isFromManager) {
      const existingIndex = paymentTypes.value.findIndex((x) => x.id === item.id);
      if (existingIndex !== -1) {
        const existingItem = paymentTypes.value[existingIndex];
        if (existingItem.recordMode === RecordMode.Added) {
          paymentTypes.value.splice(existingIndex, 1);
        } else if (existingItem.recordMode === RecordMode.Modified) {
          existingItem.recordMode = RecordMode.Deleted;
        } else {
          existingItem.recordMode = RecordMode.Deleted;
        }
      }
    }
  });
}

function onSave(unit: DCPaymentType) {
  if (!paymentTypes.value) return;
  createEditModalActive.value = false;

  const existingIndex = paymentTypes.value?.findIndex((x) => x.id === unit.id);
  if (existingIndex !== -1) paymentTypes.value[existingIndex] = unit;
  else paymentTypes.value.push(unit);
}

const filterState = reactive<{
  query: string;
  column?: number;
  order?: 'asc' | 'desc';
  isMapped?: boolean;
}>({
  query: '',
  column: undefined,
  order: undefined,
  isMapped: undefined,
});

async function onRefreshData(
  options: TableFilterData & {
    isMapped?: boolean;
  }
) {
  filterState.query = options.query ?? '';
  if (options.column !== undefined && options.order) {
    filterState.column = options.column;
    filterState.order = options.order;
  }
  filterState.isMapped = options.isMapped;

  await loadPaymentTypes(options);
}

async function onApplyFilters(data: RepresentativeFiltersData) {
  data.isMapped = filterIsMapped.value;
  await onRefreshData(data);
}

async function onResetFilters() {
  filterIsMapped.value = undefined;
  await onRefreshData({});
}
</script>
